import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'react-bootstrap/Dropdown';
import LogService from '@app/services/log.service';
import { actualizarTicketNotify, actualizarComentarioNotify } from '@app/store/slices/default.slice';
import NotificationCardM3 from './NotificationCardM3';

/**
 * Panel lateral de notificaciones.
 *
 * - Modo "drawer" (default): overlay con animacion slide desde la derecha (mobile / md).
 * - Modo "pinned": panel fijo persistente a la derecha del layout (lg / xl); no overlay ni backdrop.
 * - Tabs Tickets / Comentarios (paginado independiente).
 * - Infinite scroll: carga la siguiente pagina al llegar cerca del final del listado.
 * - Menu kebab con "marcar como leido".
 * - Refresco en tiempo real: cuando cambian los contadores en Redux (via sockets)
 *   se recarga la primera pagina del tab activo (y del inactivo cuando esta pinneado).
 *
 * Props:
 *   isOpen     boolean               -> visible u oculto (montado siempre para animar en drawer).
 *   onClose    fn                    -> se llama al pulsar cerrar / backdrop / ESC.
 *   variant    'drawer' | 'pinned'   -> modo de despliegue (default 'drawer').
 *   width      number                -> ancho en px del panel (default 400).
 *   topOffset  number                -> offset superior en px (para modo pinned; default 57).
 */

const TabButton = ({ active, onClick, icon, label, count, activeColor }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 12,
            border: 'none',
            background: active ? activeColor : 'transparent',
            color: active ? '#fff' : '#4B5563',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            transition: 'background-color .15s ease, color .15s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
        }}
    >
        <i className={icon} />
        {label}
        {count > 0 && (
            <span
                style={{
                    background: active ? 'rgba(255,255,255,0.28)' : '#EF4444',
                    color: '#fff',
                    borderRadius: 999,
                    padding: '1px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    minWidth: 22,
                    textAlign: 'center'
                }}
            >
                {count}
            </span>
        )}
    </button>
);

const getInitialState = () => ({
    items: [],
    page: 0,      // proxima pagina a solicitar (0-indexed)
    totalPages: null,
    isLoading: false,
    hasError: false
});

const NotificationsDrawer = ({
    isOpen,
    onClose,
    variant = 'drawer',
    width = 400,
    topOffset = 57
}) => {
    const dispatch = useDispatch();
    const ticketNotify = useSelector((state) => state.default.ticketNotify);
    const comentarioNotify = useSelector((state) => state.default.comentarioNotify);

    const isPinned = variant === 'pinned';
    // El panel esta "activo" (montado y visible) unicamente cuando esta abierto.
    // En modo drawer se mantiene montado para animar el slide-out (return null solo pinned).
    const isActive = isOpen;

    const [tab, setTab] = useState('ticket');
    const [ticketState, setTicketState] = useState(getInitialState);
    const [comentState, setComentState] = useState(getInitialState);

    const scrollRef = useRef(null);

    // Estado activo segun tab
    const activeState = tab === 'ticket' ? ticketState : comentState;

    const contadores = useMemo(() => ({
        ticket: ticketNotify?.count || 0,
        comentarios: comentarioNotify?.count || 0
    }), [ticketNotify, comentarioNotify]);

    // ---- Cierre con ESC (solo modo drawer) ------------------------------
    useEffect(() => {
        if (isPinned) return undefined;
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose, isPinned]);

    // ---- Bloquear scroll del body al abrir (solo modo drawer) -----------
    useEffect(() => {
        if (isPinned) return undefined;
        if (!isOpen) return undefined;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = original; };
    }, [isOpen, isPinned]);

    // ---- Fetch generico -------------------------------------------------
    const fetchPage = useCallback(async (targetTab, targetPage, replace = false) => {
        const setter = targetTab === 'ticket' ? setTicketState : setComentState;
        const service = targetTab === 'ticket' ? LogService.getAllTicketNotify : LogService.getAllComentarioNotify;
        setter((prev) => ({ ...prev, isLoading: true, hasError: false }));
        try {
            const resp = await service({ page: targetPage });
            const content = Array.isArray(resp?.content) ? resp.content : [];
            setter((prev) => ({
                items: replace ? content : [...prev.items, ...content],
                page: targetPage + 1,
                totalPages: resp?.totalPages ?? prev.totalPages ?? 0,
                isLoading: false,
                hasError: false
            }));
        } catch (err) {
            setter((prev) => ({ ...prev, isLoading: false, hasError: true }));
        }
    }, []);

    // ---- Carga inicial al abrir / al pinnear ----------------------------
    useEffect(() => {
        if (!isActive) {
            // Al cerrar el drawer reseteamos el tracking de contadores para
            // que la proxima apertura no dispare un refetch redundante.
            // eslint-disable-next-line no-use-before-define
            lastCountsRef.current = null;
            return;
        }
        // Reseteo total para reflejar siempre el estado actual del backend.
        setTicketState(getInitialState());
        setComentState(getInitialState());
        fetchPage('ticket', 0, true);
        fetchPage('comentarios', 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    // ---- Refresco en tiempo real (sockets -> redux counts) --------------
    // Cuando cambian los contadores en Redux (disparados por sockets en
    // UserDropdown), recargamos la primera pagina para reflejar el nuevo
    // item / cambio de estado sin necesidad de reabrir el panel.
    const ticketCount = ticketNotify?.count;
    const comentCount = comentarioNotify?.count;
    const lastCountsRef = useRef(null);
    useEffect(() => {
        if (!isActive) return;
        const prev = lastCountsRef.current;
        lastCountsRef.current = { t: ticketCount, c: comentCount };
        // Primera ejecucion tras abrir: la carga inicial ya la hace el otro efecto.
        if (prev === null) return;
        if (prev.t === ticketCount && prev.c === comentCount) return;
        fetchPage('ticket', 0, true);
        fetchPage('comentarios', 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketCount, comentCount, isActive]);

    // ---- Cambio de tab: reset scroll ------------------------------------
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [tab]);

    // ---- Infinite scroll ------------------------------------------------
    const onScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (!el) return;
        if (activeState.isLoading) return;
        if (activeState.totalPages === null) return;
        if (activeState.page >= activeState.totalPages) return;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
        if (nearBottom) {
            fetchPage(tab, activeState.page, false);
        }
    }, [activeState, fetchPage, tab]);

    // ---- Acciones "marcar como leido" -----------------------------------
    const marcarLeidosTickets = async () => {
        await LogService.ticketNotifyViewAll();
        dispatch(actualizarTicketNotify());
        setTicketState(getInitialState());
        fetchPage('ticket', 0, true);
    };
    const marcarLeidosComentarios = async () => {
        await LogService.comentarioNotifyViewAll();
        dispatch(actualizarComentarioNotify());
        setComentState(getInitialState());
        fetchPage('comentarios', 0, true);
    };
    const marcarTodoLeido = async () => {
        await LogService.notifyViewAll();
        dispatch(actualizarTicketNotify());
        dispatch(actualizarComentarioNotify());
        setTicketState(getInitialState());
        setComentState(getInitialState());
        fetchPage('ticket', 0, true);
        fetchPage('comentarios', 0, true);
    };

    // ---- Render ---------------------------------------------------------
    const activeTabColor = tab === 'ticket' ? '#1976D2' : '#7C3AED';

    // En modo pinned no rendereamos backdrop, y el aside es fijo al costado
    // derecho, dentro del layout (ver Main.js: content-wrapper recibe margin-right).
    const asideStyle = isPinned
        ? {
            position: 'fixed',
            top: topOffset,
            right: 0,
            bottom: 0,
            width,
            maxWidth: width,
            backgroundColor: '#F8FAFC',
            boxShadow: '-4px 0 16px rgba(15,23,42,0.08)',
            borderLeft: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1050
        }
        : {
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: '100%',
            maxWidth: width,
            backgroundColor: '#F8FAFC',
            boxShadow: '-8px 0 24px rgba(15,23,42,0.15)',
            transform: isOpen ? 'translateX(0)' : 'translateX(105%)',
            transition: 'transform .25s ease',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1081
        };

    // En modo pinned el panel solo se monta cuando isOpen es true (bell toggle).
    // En modo drawer siempre se monta para poder animar el slide-out.
    if (isPinned && !isOpen) return null;

    return (
        <>
            {/* Backdrop solo en modo drawer */}
            {!isPinned && (
                <div
                    onClick={onClose}
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.35)',
                        opacity: isOpen ? 1 : 0,
                        pointerEvents: isOpen ? 'auto' : 'none',
                        transition: 'opacity .2s ease',
                        zIndex: 1080
                    }}
                />
            )}

            {/* Panel */}
            <aside
                role={isPinned ? 'complementary' : 'dialog'}
                aria-modal={isPinned ? undefined : 'true'}
                aria-hidden={isPinned ? undefined : !isOpen}
                aria-label="Notificaciones"
                style={asideStyle}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '18px 18px 14px 18px',
                        background: '#fff',
                        borderBottom: '1px solid #E5E7EB'
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                            <div
                                style={{
                                    width: 36, height: 36, borderRadius: 12,
                                    background: '#E3F2FD', color: '#1976D2',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 16
                                }}
                            >
                                <i className="fas fa-bell" />
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, color: '#111827', fontSize: 16 }}>
                                    Notificaciones
                                </div>
                                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    {`${contadores.ticket + contadores.comentarios} sin leer`}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: 4 }}>
                            <Dropdown align="end">
                                <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    id="notif-drawer-menu"
                                    style={{
                                        borderRadius: 10,
                                        border: '1px solid #E5E7EB',
                                        background: '#fff',
                                        color: '#4B5563',
                                        padding: '6px 10px'
                                    }}
                                >
                                    <i className="fas fa-ellipsis-v" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={marcarLeidosTickets}>
                                        <i className="fas fa-check mr-2 text-muted" />
                                        Marcar tickets como leidos
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={marcarLeidosComentarios}>
                                        <i className="fas fa-check mr-2 text-muted" />
                                        Marcar comentarios como leidos
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={marcarTodoLeido}>
                                        <i className="fas fa-check-double mr-2 text-success" />
                                        Marcar todo como leido
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Cerrar"
                                style={{
                                    borderRadius: 10,
                                    border: '1px solid #E5E7EB',
                                    background: '#fff',
                                    color: '#4B5563',
                                    padding: '6px 10px',
                                    cursor: 'pointer'
                                }}
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div
                        style={{
                            display: 'flex',
                            gap: 6,
                            padding: 4,
                            borderRadius: 14,
                            background: '#F1F5F9'
                        }}
                    >
                        <TabButton
                            active={tab === 'ticket'}
                            onClick={() => setTab('ticket')}
                            icon="fas fa-ticket-alt"
                            label="TICKETS"
                            count={contadores.ticket}
                            activeColor="#1976D2"
                        />
                        <TabButton
                            active={tab === 'comentarios'}
                            onClick={() => setTab('comentarios')}
                            icon="fas fa-comment-alt"
                            label="COMENTARIOS"
                            count={contadores.comentarios}
                            activeColor="#1976D2"
                        />
                    </div>
                </div>

                {/* Contenido con scroll */}
                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '12px 14px 20px 14px'
                    }}
                >
                    {activeState.items.length === 0 && !activeState.isLoading && !activeState.hasError && (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '48px 12px',
                                color: '#6B7280'
                            }}
                        >
                            <i className="far fa-bell-slash" style={{ fontSize: 32, opacity: 0.4, marginBottom: 12, display: 'block' }} />
                            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                                Sin notificaciones
                            </div>
                            <small>Aun no registra actividad.</small>
                        </div>
                    )}

                    {activeState.hasError && activeState.items.length === 0 && (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '32px 12px',
                                color: '#B91C1C'
                            }}
                        >
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
                            No se pudieron cargar las notificaciones.
                            <div className="mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => fetchPage(tab, 0, true)}
                                >
                                    Reintentar
                                </button>
                            </div>
                        </div>
                    )}

                    {activeState.items.map((item) => (
                        <NotificationCardM3
                            key={tab === 'ticket' ? `t-${item.id_tkntf}` : `c-${item.id_comntf}`}
                            type={tab === 'ticket' ? 'ticket' : 'comentario'}
                            item={item}
                            onNavigate={onClose}
                        />
                    ))}

                    {/* Loader inferior (paginacion) */}
                    {activeState.isLoading && (
                        <div style={{ textAlign: 'center', padding: '14px 0', color: '#6B7280', fontSize: 13 }}>
                            <i className="fas fa-spinner fa-spin mr-2" />
                            Cargando...
                        </div>
                    )}

                    {/* Fin del listado */}
                    {!activeState.isLoading
                        && activeState.items.length > 0
                        && activeState.totalPages !== null
                        && activeState.page >= activeState.totalPages && (
                        <div style={{ textAlign: 'center', padding: '10px 0', color: '#9CA3AF', fontSize: 12 }}>
                            No hay mas notificaciones
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
};

export default NotificationsDrawer;
